/**
 * DAILY TAROT WIDGET — Viral Dashboard Card
 * Shows "Card of the Day" inline + 1-tap WhatsApp share with referral.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, MessageCircle, RefreshCw, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const DailyTarotWidget = () => {
  const { user } = useAuth();
  const [draw, setDraw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/anant-tarot/status');
      setDraw(r.data.free_card_today);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  const drawNow = async () => {
    setDrawing(true);
    try {
      const r = await api.post('/anant-tarot/draw-free');
      setDraw(r.data.draw);
    } catch (e) { /* silent */ }
    finally { setDrawing(false); }
  };

  const shareWhatsapp = () => {
    if (!draw) return;
    const referralCode = user?.username || '';
    // Use BACKEND OG URL for the share link so WhatsApp/Telegram scrapers receive
    // proper og:image = the tarot card (not the SPA's default Anant logo meta).
    // The backend endpoint auto-redirects browsers to the React card detail page.
    const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const cardSlug = draw.card.slug ?? draw.card.cardId ?? draw.card.id;
    const refQuery = referralCode ? `?ref=${encodeURIComponent(referralCode)}` : '';
    const shareUrl = `${backendBase}/api/anant-tarot/card-og/${cardSlug}${refQuery}`;
    const text = `🔮 My Anant Card of the Day: *${draw.card.name}*\n\n"${draw.card.basic_meaning}"\n\n${shareUrl}\n\n✨ Draw yours free at Anant Inner Self.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-fuchsia-400/30 bg-gradient-to-br from-violet-950 via-fuchsia-950 to-indigo-950 p-4">
        <div className="h-20" />
      </div>
    );
  }

  return (
    <div data-testid="daily-tarot-widget" className="relative overflow-hidden rounded-2xl border border-fuchsia-400/30 bg-gradient-to-br from-violet-950 via-fuchsia-950 to-indigo-950 p-4 sm:p-5">
      <div className="relative flex items-center gap-4">
        {/* Card image */}
        {draw ? (
          <Link to="/anant-tarot" className="flex-shrink-0 w-20 sm:w-24 aspect-[2/3] rounded-lg overflow-hidden border-2 border-amber-400/40 shadow-lg shadow-fuchsia-500/30 hover:scale-105 transition">
            <img src={draw.card.image_url} alt={draw.card.name} className="w-full h-full object-cover" />
          </Link>
        ) : (
          <button onClick={drawNow} disabled={drawing} data-testid="widget-draw-btn" className="flex-shrink-0 w-20 sm:w-24 aspect-[2/3] rounded-lg bg-gradient-to-br from-violet-800 via-fuchsia-800 to-indigo-900 border-2 border-fuchsia-400/40 flex items-center justify-center shadow-lg hover:scale-105 transition group">
            {drawing ? <RefreshCw className="w-6 h-6 text-fuchsia-300 animate-spin" /> : <Sparkles className="w-8 h-8 text-fuchsia-300 group-hover:animate-pulse" />}
          </button>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-fuchsia-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Card of the Day
          </div>
          {draw ? (
            <>
              <div className="text-white font-extrabold text-base sm:text-lg leading-tight mt-0.5">{draw.card.name}</div>
              <div className="text-fuchsia-100/70 text-xs sm:text-sm mt-1 line-clamp-2 italic">&ldquo;{draw.card.basic_meaning}&rdquo;</div>
              <div className="flex gap-2 mt-2 flex-wrap">
                <button
                  onClick={shareWhatsapp}
                  data-testid="widget-share-whatsapp"
                  className="px-2.5 py-1 rounded-lg bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-300 text-[11px] font-semibold flex items-center gap-1 transition"
                >
                  <MessageCircle className="w-3 h-3" /> Share to WhatsApp
                </button>
                <Link to="/anant-tarot" className="px-2.5 py-1 rounded-lg bg-fuchsia-500/20 hover:bg-fuchsia-500/30 border border-fuchsia-500/30 text-fuchsia-300 text-[11px] font-semibold flex items-center gap-1 transition">
                  Full Reading <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="text-white font-bold text-base sm:text-lg mt-0.5">A free message from your inner self 🔮</div>
              <div className="text-fuchsia-100/60 text-xs mt-1">Tap the card to draw your daily guidance.</div>
              <button onClick={drawNow} disabled={drawing} className="mt-2 px-3 py-1 rounded-lg bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white text-[11px] font-bold disabled:opacity-50">
                {drawing ? 'Drawing…' : 'Draw Now'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyTarotWidget;
